import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './DatePicker.css';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const WEEK_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

function toDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function DatePicker({ selectedDate, onDateChange, label, minDate, maxDate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = toDate(selectedDate) || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768,
  );
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const d = toDate(selectedDate);
    if (!d) return;
    setCurrentMonth((prev) => {
      if (prev.getFullYear() === d.getFullYear() && prev.getMonth() === d.getMonth()) return prev;
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });
  }, [selectedDate]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || isMobile) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 260;
    const height = panelRef.current?.offsetHeight || 280;
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    left = Math.max(8, left);
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - 8 && rect.top > height + 12) {
      top = rect.top - height - 6;
    }
    top = Math.max(8, Math.min(top, window.innerHeight - height - 8));
    setPos({ top, left });
  }, [isMobile]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    const t = window.setTimeout(updatePosition, 0);
    const onWin = () => updatePosition();
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    const prev = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
      document.body.style.overflow = prev;
    };
  }, [isOpen, currentMonth, isMobile, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [isOpen]);

  const formatDateDisplay = (date) => {
    const parsed = toDate(date);
    if (!parsed) return 'Выберите дату';
    const target = startOfDay(parsed);
    const today = startOfDay(new Date());
    if (target.getTime() === today.getTime()) return 'Сегодня';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.getTime() === yesterday.getTime()) return 'Вчера';
    return target.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
    return days;
  };

  const isDisabled = (date) => {
    if (!date) return true;
    if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
    if (maxDate && startOfDay(date) > startOfDay(maxDate)) return true;
    return false;
  };

  const selectDate = (date) => {
    if (!date || isDisabled(date)) return;
    onDateChange?.(date);
    setIsOpen(false);
  };

  const goToday = () => {
    const today = startOfDay(new Date());
    if (isDisabled(today)) return;
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    onDateChange?.(today);
    setIsOpen(false);
  };

  const days = getDaysInMonth(currentMonth);
  const today = startOfDay(new Date());

  const panel = isOpen
    ? createPortal(
        <>
          <div className="dp-overlay" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div
            ref={panelRef}
            className={`dp-panel${isMobile ? ' dp-panel--sheet' : ''}`}
            style={isMobile ? undefined : { top: pos.top, left: pos.left }}
            role="dialog"
            aria-label="Выбор даты"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dp-header">
              <button
                type="button"
                className="dp-nav"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                aria-label="Предыдущий месяц"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <div className="dp-month">
                {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </div>
              <button
                type="button"
                className="dp-nav"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                aria-label="Следующий месяц"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>

            <div className="dp-weekdays">
              {WEEK_DAYS.map((d) => (
                <div key={d} className="dp-weekday">{d}</div>
              ))}
            </div>

            <div className="dp-days">
              {days.map((day, index) => {
                if (!day) return <div key={`e-${index}`} className="dp-day dp-day--empty" />;
                const selected = sameDay(day, toDate(selectedDate));
                const isToday = sameDay(day, today);
                const disabled = isDisabled(day);
                return (
                  <button
                    key={`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`}
                    type="button"
                    className={[
                      'dp-day',
                      isToday ? 'dp-day--today' : '',
                      selected ? 'dp-day--selected' : '',
                      disabled ? 'dp-day--disabled' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => selectDate(day)}
                    disabled={disabled}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="dp-footer">
              <button type="button" className="dp-today" onClick={goToday}>Сегодня</button>
            </div>
          </div>
        </>,
        document.body,
      )
    : null;

  return (
    <div className="dp-root" ref={triggerRef}>
      <button
        type="button"
        className={`dp-trigger${isOpen ? ' is-open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span>{label || formatDateDisplay(selectedDate)}</span>
        <svg className="dp-trigger-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {panel}
    </div>
  );
}

export default DatePicker;
